import { useQuery } from "@apollo/client";
import { Navigate } from "react-router-dom";
import usePageTitle from "@/hooks/usePageTitle";
import AppLayout from "@/layouts/AppLayout";
import Breadcrumb from "@/components/Breadcrumb";
import Tabs from "@/components/Tabs";
import { TableHeading } from "@/components/Table";
import PageHeading from "@/components/PageHeading/PageHeading";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import usePagination from "@/hooks/usePagination";
import useCurrentBackofficeUser from "@/hooks/useCurrentBackofficeUser";
import ApplicationsTable from "./_components/ApplicationsTable";
import useApplicationFilters from "./_hooks/useApplicationFilters";
import {
  MEMBERSHIP_APPLICATIONS_QUERY,
  type MembershipApplicationsQueryResponse,
  type MembershipApplicationsQueryVariables
} from "./queries";
import { useMembershipServicesNavLinks } from "../../_membershipServicesNavLinks";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import { useNavigate, type Path } from "@/router";
import type { ApplicationTab, ApplicationType } from "./types";

export type { ApplicationTab };

const APPLICATION_TABS: { label: string; path: ApplicationTab; applicationType?: ApplicationType }[] = [
  { label: "All Applications", path: "all" },
  { label: "Affiliate", path: "affiliate", applicationType: "affiliate" },
  { label: "Associate", path: "associate", applicationType: "associate" }
  // Member and Reciprocity tabs are intentionally hidden for now; re-enable behind a feature flag later.
];

interface ApplicationsPageProps {
  activeTab: ApplicationTab;
}

export function ApplicationsPageContent({ activeTab }: ApplicationsPageProps) {
  useRequireCapability({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageApplications
  });
  usePageTitle(`/people/membership-services/applications/${activeTab}`, "Applications");
  const navigate = useNavigate();
  const navLinks = useMembershipServicesNavLinks();
  const { Menu, setMenu } = useTableRowMenu();
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const currentUser = useCurrentBackofficeUser();

  const filters = useApplicationFilters({
    resetPagination,
    currentUserId: currentUser?.id
  });

  const tabApplicationType = APPLICATION_TABS.find((tab) => tab.path === activeTab)?.applicationType;
  const applicationType = tabApplicationType ?? filters.queryVariables.typeFilter;

  const {
    data: currentData,
    previousData,
    loading
  } = useQuery<MembershipApplicationsQueryResponse, MembershipApplicationsQueryVariables>(
    MEMBERSHIP_APPLICATIONS_QUERY,
    {
      variables: {
        ...paginationVariables,
        applicationType,
        status: filters.queryVariables.status,
        search: filters.queryVariables.search,
        sectionId: filters.queryVariables.sectionId,
        reviewerId: filters.queryVariables.reviewerId,
        unassigned: filters.queryVariables.unassigned,
        submittedAfter: filters.queryVariables.submittedAfter
      },
      skip: filters.awaitingAssignedReviewer
    }
  );

  const awaitingAssignedReviewer = filters.awaitingAssignedReviewer;
  const data = awaitingAssignedReviewer ? undefined : currentData || previousData;
  const nodes = data?.membershipApplications.nodes || [];
  const tableLoading = loading || awaitingAssignedReviewer;

  return (
    <AppLayout interior bottomBar navLinks={navLinks}>
      <Breadcrumb
        title="Operations"
        compactTitle="Application Management"
        backLink={{ to: "/people/membership-services" }}
        compact
      />
      <PageHeading title="Applications" compact />
      <Tabs
        tabItems={APPLICATION_TABS.map((tab) => ({
          label: tab.label,
          link: `/people/membership-services/applications/${tab.path}` as Path
        }))}
      />
      <Menu />
      <div className="w-full">
        <TableHeading
          heading="Applications"
          body="Review and process all types of membership program applications"
          actions={[]}
        />
        <ApplicationsTable
          loading={tableLoading}
          data={nodes}
          setMenu={setMenu}
          createHeaderPagination={createHeaderPagination(data?.membershipApplications)}
          createFooterPagination={createFooterPagination(data?.membershipApplications)}
          activeTab={activeTab}
          filters={{
            searchValue: filters.searchValue,
            setSearchValue: filters.setSearchValue,
            typeFilter: filters.typeFilter,
            setTypeFilter: filters.setTypeFilter,
            stageFilter: filters.stage,
            setStageFilter: filters.setStage,
            sectionFilter: filters.sectionId,
            setSectionFilter: filters.setSectionId,
            submittedOnFilter: filters.submittedOn,
            setSubmittedOnFilter: filters.setSubmittedOn,
            assigned: filters.assigned,
            toggleAssigned: filters.toggleAssigned,
            filterState: filters.filterState,
            activeFilterCount: filters.activeFilterCount,
            onClearAllFilters: filters.clearAllFilters
          }}
          onViewApplication={(applicationId) => {
            navigate("/people/membership-services/applications/:applicationId", {
              params: { applicationId }
            });
          }}
        />
      </div>
    </AppLayout>
  );
}

export default function ApplicationsIndexPage() {
  return <Navigate to="/people/membership-services/applications/all" replace />;
}
